import axios from "./axios";

/**
 * @param {number|string} projectId
 * @param {object} [options] axios config — use `params` for query (e.g. selectable_for_work_order, work_order_id);
 *   set `throwOnInvalidResponse` when an empty error response must not be treated as an empty activity list
 * @returns {Promise<Array>} project activity rows
 */
export async function fetchProjectActivities(projectId, options = {}) {
  if (!projectId) return [];
  const { throwOnInvalidResponse = false, ...config } = options;
  const response = await axios.get(`/projects/${projectId}/project_activities`, config);
  const payload = response.data;
  const hasErrors = Array.isArray(payload?.errors) ? payload.errors.length > 0 : Boolean(payload?.errors);
  if (throwOnInvalidResponse && (response.status >= 400 || hasErrors || payload?.success === false ||
    (!Array.isArray(payload?.data) && !Array.isArray(payload)))) {
    throw new Error("Could not load project activities");
  }
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

/**
 * Create a project activity on a project (optionally link as activity_source later via work order save).
 * @param {number|string} projectId
 * @param {object} attributes — title (required), description, number, etc.
 */
export async function createProjectActivity(projectId, attributes) {
  if (!projectId) throw new Error("projectId required");
  const response = await axios.post(`/projects/${projectId}/project_activities`, {
    project_activity: attributes,
  });
  const payload = response.data;
  return payload?.data ?? payload;
}

function unwrapList(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function unwrapRecord(payload) {
  return payload?.data ?? payload;
}

/**
 * @param {number|string} projectId
 * @returns {Promise<Array>} activity groups
 */
export async function fetchProjectActivityGroups(projectId, options = {}) {
  if (!projectId) return [];
  const response = await axios.get(
    `/projects/${projectId}/project_activity_groups`,
    options
  );
  return unwrapList(response.data);
}

/**
 * @param {number|string} projectId
 * @param {object} attributes — title (required), row_order
 */
export async function createProjectActivityGroup(projectId, attributes) {
  if (!projectId) throw new Error("projectId required");
  const response = await axios.post(`/projects/${projectId}/project_activity_groups`, {
    project_activity_group: attributes,
  });
  return unwrapRecord(response.data);
}

/**
 * @param {number|string} projectId
 * @param {number|string} groupId
 * @param {object} attributes — title, row_order
 */
export async function updateProjectActivityGroup(projectId, groupId, attributes) {
  if (!projectId || !groupId) throw new Error("projectId and groupId required");
  const response = await axios.patch(
    `/projects/${projectId}/project_activity_groups/${groupId}`,
    { project_activity_group: attributes }
  );
  return unwrapRecord(response.data);
}

/**
 * Deletes a group. Activities are ungrouped (group_id nullified), not deleted.
 */
export async function deleteProjectActivityGroup(projectId, groupId) {
  if (!projectId || !groupId) throw new Error("projectId and groupId required");
  const response = await axios.delete(
    `/projects/${projectId}/project_activity_groups/${groupId}`
  );
  return unwrapRecord(response.data);
}

/**
 * Persist activity order and group membership.
 * @param {number|string} projectId
 * @param {Array<{ id, group_id, row_order }>} items
 */
export async function reorderProjectActivities(projectId, items) {
  if (!projectId) throw new Error("projectId required");
  const response = await axios.post(`/projects/${projectId}/project_activities/reorder`, {
    items,
  });
  return unwrapRecord(response.data);
}

/**
 * Persist group order. Falls back to PATCH each group if reorder is 404.
 * @param {number|string} projectId
 * @param {Array<number|string>} ids
 */
export async function reorderProjectActivityGroups(projectId, ids) {
  if (!projectId) throw new Error("projectId required");
  try {
    const response = await axios.post(
      `/projects/${projectId}/project_activity_groups/reorder`,
      { ids }
    );
    return unwrapRecord(response.data);
  } catch (error) {
    if (error?.response?.status !== 404) throw error;
    await Promise.all(
      (ids || []).map((id, row_order) =>
        updateProjectActivityGroup(projectId, id, { row_order })
      )
    );
    return { success: true, fallback: "patch" };
  }
}

/**
 * Duplicate a single project activity (new number, same group).
 * @returns {Promise<object>} created activity
 */
export async function duplicateProjectActivity(projectId, activityId) {
  if (!projectId || !activityId) throw new Error("projectId and activityId required");
  const response = await axios.post(
    `/projects/${projectId}/project_activities/${activityId}/duplicate`
  );
  return unwrapRecord(response.data);
}

/**
 * Duplicate a group and all its activities.
 * @param {number|string} projectId
 * @param {number|string} groupId
 * @param {{ title?: string }} [attributes]
 * @returns {Promise<{ group: object, activities: object[] }>}
 */
export async function duplicateProjectActivityGroup(projectId, groupId, attributes = {}) {
  if (!projectId || !groupId) throw new Error("projectId and groupId required");
  const response = await axios.post(
    `/projects/${projectId}/project_activity_groups/${groupId}/duplicate`,
    { project_activity_group: attributes }
  );
  return unwrapRecord(response.data);
}

/**
 * Download project activities as PDF (groups + activities).
 * @param {number|string} projectId
 */
export async function exportProjectActivitiesPdf(projectId) {
  if (!projectId) throw new Error("projectId required");
  const response = await axios.get(`/projects/${projectId}/project_activities/export_pdf`, {
    responseType: "blob",
    headers: { Accept: "application/pdf" },
  });

  if (response.data.type && response.data.type !== "application/pdf") {
    const text = await response.data.text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.errors?.[0] || json.message || "Invalid PDF response");
    } catch (error) {
      if (error.message !== "Invalid PDF response") throw error;
      throw new Error("Response is not a PDF file");
    }
  }

  const arrayBuffer = await response.data.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const pdfHeader = String.fromCharCode(...uint8Array.slice(0, 4));
  if (pdfHeader !== "%PDF") {
    throw new Error("Invalid PDF file");
  }

  const blob = new Blob([arrayBuffer], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `prosjektaktiviteter_${projectId}_${new Date().toISOString().split("T")[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
